import { ApplicationGroup, MigrationWave } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, ColumnLayout, Container, Grid, Header, Popover, ProgressBar, SpaceBetween, StatusIndicator} from '@amzn/awsui-components-react';
import { useState } from 'react';

import { toUTCShortDate } from '../../../common/DateDisplay';
import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { CreateOrEditWaveState, DeleteWaveState, RemoveFromWaveState } from './interface';

type Props = FlashContextInterface & {
    wave: MigrationWave;
    setRemoveFromWaveDetails: (details: RemoveFromWaveState) => void;
    setDeleteWaveDetails: (details: DeleteWaveState) => void;
    setEditWaveDetails: (details: CreateOrEditWaveState) => void;
};

function MigrationWaveReport({ wave, setRemoveFromWaveDetails, setDeleteWaveDetails, setEditWaveDetails }: Props) {
    const [expanded, setExpanded] = useState(false);
    // display the date in UTC to be consistent across timezones
    const startDate = wave.startDate ? toUTCShortDate(new Date(wave.startDate)): constants.NA;
    const endDate = wave.endDate ? toUTCShortDate(new Date(wave.endDate)): constants.NA;
    const columnDefinitions: ColumnDefinition<ApplicationGroup>[] = [{
        id: 'groupName',
        header: constants.GROUP_NAME,
        data: item => item.name,
        cell: item => item.name,
        type: ColumnTypes.TEXT,
    }, {
        id: 'applications',
        header: constants.APPLICATIONS,
        data: item => item.totalApplications || 0,
        cell: item => item.totalApplications || 0,
        type: ColumnTypes.NUMBER,
    }, {
        id: 'servers',
        header: constants.SERVERS,
        data: item => item.totalServers || 0,
        cell: item => item.totalServers || 0,
        type: ColumnTypes.NUMBER,
    }, {
        id: 'databases',
        header: constants.DATABASES,
        data: item => item.totalDatabases || 0,
        cell: item => item.totalDatabases || 0,
        type: ColumnTypes.NUMBER,
    }, {
        id: 'ranking',
        header: constants.RANKING,
        data: item => item.ranking || 0,
        cell: item => item.ranking || 0,
        type: ColumnTypes.NUMBER,
    }, {
        id: 'removeFromWave',
        header: '',
        data: item => item.name,
        cell: item => <Button onClick={() => setRemoveFromWaveDetails({ visible: true, groupId: item.applicationGroupId,
            groupName: item.name, waveId: wave.migrationWaveId, waveName: wave.name })}>{constants.REMOVE_BUTTON_TEXT}</Button>,
        type: ColumnTypes.NUMBER,
    }];
    const applicationProgress = wave.applicationCapacity ? (wave.totalApplications || 0) / wave.applicationCapacity * 100 : 0;
    const serverProgress = wave.serverCapacity ? (wave.totalServers || 0) / wave.serverCapacity * 100 : 0;
    const databaseProgress = wave.databaseCapacity ? (wave.totalDatabases || 0) / wave.databaseCapacity * 100 : 0;
    return (
        <Container disableContentPaddings disableHeaderPaddings className='migration-wave-report' header={
            <Box className='migration-wave-report-header'>
                <Header
                    actions={
                        <SpaceBetween direction='horizontal' size='l'>
                            <Button
                                variant='icon'
                                iconName='edit'
                                onClick={() => setEditWaveDetails({visible: true, waveToEdit: wave, isInvalid: false})} />
                            <Button
                                variant='icon'
                                iconName='close'
                                onClick={() => setDeleteWaveDetails({visible: true, waveName: wave.name, waveId: wave.migrationWaveId})} />
                        </SpaceBetween>
                    }
                    id={wave.migrationWaveId}
                    description={`${startDate} - ${endDate} ${wave.comment || ''}`}>
                    <Button
                        data-testid='expand-btn'
                        variant='icon'
                        iconName={expanded ? 'caret-down-filled' : 'caret-right-filled'}
                        onClick={() => setExpanded(prev => !prev)}>
                    </Button>
                    {wave.name}
                </Header>
            </Box> }>
            { expanded ? (
                <Grid gridDefinition={[{colspan: 9}, {colspan: 3}]} disableGutters>
                    <Box>
                        <ColumnLayout columns={2} variant='text-grid'>
                            <Box padding='xxl'>
                                <SpaceBetween size='m'>
                                    {<Box variant='h3'>{constants.ASSET_CAPACITY}</Box>}
                                    <ProgressBar variant='key-value' description={constants.APPLICATIONS} value={applicationProgress}></ProgressBar>
                                    <ProgressBar variant='key-value' description={constants.SERVERS} value={serverProgress}></ProgressBar>
                                    <ProgressBar variant='key-value' description={constants.DATABASES} value={databaseProgress}></ProgressBar>
                                </SpaceBetween>
                            </Box>
                            <Box padding='xxl'>
                                <SpaceBetween size='m'>
                                    <SpaceBetween direction='horizontal' size='xs'>
                                        {<Box variant='h3'>{constants.DATA_VALIDATION_PASSED}</Box>}
                                        {<Popover
                                            dismissButton={false}
                                            content={constants.MODIFY_RULES_FOR_VALIDATION}>
                                            <StatusIndicator type='info'></StatusIndicator>
                                        </Popover>}
                                    </SpaceBetween>
                                    <ProgressBar variant='key-value' description={constants.APPLICATIONS} value={wave.avgApplicationDataCollectedPct || 0} />
                                    <ProgressBar variant='key-value' description={constants.SERVERS} value={wave.avgServerDataCollectedPct || 0} />
                                    <ProgressBar variant='key-value' description={constants.DATABASES} value={wave.avgDatabaseDataCollectedPct || 0} />
                                </SpaceBetween>
                            </Box>
                        </ColumnLayout>
                        <AWSCATTable resourceName={constants.GROUPS} columnDefinitions={columnDefinitions} items={wave.groups} />
                    </Box>
                    <Box padding='l' className='migration-right-side-panel'>
                        <SpaceBetween size='l'>
                            <h3>{constants.DETAILS}</h3>
                            <div><Box color='text-label' margin={{bottom: 'xxxs'}}>{constants.MIGRATION_DATES}</Box>{startDate} - {endDate}</div>
                            <div><Box color='text-label' margin={{bottom: 'xxxs'}}>{constants.STATUS}</Box>
                                {wave.closed ? constants.PLANNED : constants.IN_PLANNING}</div>
                            <div>
                                <Box color='text-label' margin={{bottom: 'xxxs'}}>{constants.APPLICATIONS}</Box>
                                <Box>{constants.TOTAL}: { wave.totalApplications || 0 }</Box>
                                <Box>{constants.WAVE_CAPACITY}: { wave.applicationCapacity || 0 }</Box>
                            </div>
                            <div>
                                <Box color='text-label' margin={{bottom: 'xxxs'}}>{constants.SERVERS}</Box>
                                <Box>{constants.TOTAL}: { wave.totalServers || 0 }</Box>
                                <Box>{constants.WAVE_CAPACITY}: { wave.serverCapacity || 0 }</Box>
                            </div>
                            <div>
                                <Box color='text-label' margin={{bottom: 'xxxs'}}>{constants.DATABASES}</Box>
                                <Box>{constants.TOTAL}: { wave.totalDatabases || 0 }</Box>
                                <Box>{constants.WAVE_CAPACITY}: { wave.databaseCapacity || 0 }</Box>
                            </div>
                            <div><Box color='text-label' margin={{bottom: 'xxxs'}}>{constants.COMMENTS}</Box>{wave.comment}</div>
                        </SpaceBetween>
                    </Box>
                </Grid>
            ) : null }
        </Container>
    );
}

export default withFlashContext(MigrationWaveReport);
